import { useState, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { format } from 'date-fns'
import locale from 'date-fns/locale/es'
import cx from 'classnames'
import styles from './BatchSessionsModal.module.scss'


const formatSlotDate = (day, time) => {
  try {
    const d = new Date(`${day}T12:00`)
    const weekday = format(d, 'EEEE', { locale })
    const capitalized = weekday.charAt(0).toUpperCase() + weekday.slice(1)
    return `${capitalized} · ${d.toLocaleDateString()} · ${time}`
  } catch {
    return `${day} · ${time}`
  }
}

const BatchSessionsModal = ({ show, slots = [], loading, onClose, onConfirm, onSlotChange, fetchAvailability }) => {
  const { t } = useTranslation()
  const dateInputRef = useRef(null)
  // Bug real (auditoría 2026-08-16): calculado a nivel de módulo (una sola
  // vez al importar), quedaba stale para siempre en una pestaña que cruza
  // la medianoche — el min del datepicker nunca se actualizaba sin
  // recargar la página. Se recalcula por render.
  const today = new Date().toLocaleDateString('fr-ca')

  const reasonLabel = reason => ({
    holiday:     t('reason_holiday',     'Feriado'),
    lock:        t('reason_lock',        'Horario bloqueado'),
    no_capacity: t('reason_no_capacity', 'Turno ocupado'),
    no_schedule: t('reason_no_schedule', 'Fuera de horario configurado'),
  }[reason] ?? t('reason_unavailable', 'No disponible'))
  const [editingIndex, setEditingIndex] = useState(null)
  const [editDay, setEditDay] = useState('')
  const [editTime, setEditTime] = useState('')
  const [editTimes, setEditTimes] = useState([])
  const [editLoading, setEditLoading] = useState(false)
  // Bug real (auditoría 2026-08-14): openEdit/handleDayChange disparan
  // loadAvailability sin ningún guard de orden — si el usuario cambia de
  // día rápido, 2 fetches quedan en vuelo y nada garantiza el orden de
  // red; el que responda ÚLTIMO pisa editTimes, aunque sea el del día
  // viejo. requestSeqRef descarta cualquier respuesta que no sea la del
  // pedido más reciente.
  const requestSeqRef = useRef(0)

  if (!show) return null

  const available = slots.filter(s => s.available)
  const hasConflicts = available.length < slots.length
  const allUnavailable = slots.length > 0 && available.length === 0

  const openEdit = async (index, slotDay, currentTime = '') => {
    if (editingIndex === index) {
      setEditingIndex(null)
      return
    }
    setEditingIndex(index)
    setEditDay(slotDay)
    setEditTime(currentTime)
    setEditTimes([])
    await loadAvailability(slotDay)
  }

  const loadAvailability = async (day) => {
    if (!day || !fetchAvailability) return
    const seq = ++requestSeqRef.current
    setEditLoading(true)
    try {
      const result = await fetchAvailability(day)
      if (seq !== requestSeqRef.current) return
      const times = result?.slots?.[day] ? Object.keys(result.slots[day]) : []
      setEditTimes(times)
    } catch {
      if (seq === requestSeqRef.current) setEditTimes([])
    } finally {
      if (seq === requestSeqRef.current) setEditLoading(false)
    }
  }

  const handleDayChange = async (day) => {
    setEditDay(day)
    setEditTime('')
    setEditTimes([])
    if (day) await loadAvailability(day)
  }

  const handleTimeSelect = (time) => {
    setEditTime(time)
  }

  const confirmReschedule = () => {
    if (editingIndex === null || !editDay || !editTime) return
    onSlotChange(editingIndex, { day: editDay, time: editTime, available: true, rescheduled: true })
    setEditingIndex(null)
    setEditDay('')
    setEditTime('')
    setEditTimes([])
  }

  const modal = (
    <>
      <div className={styles.backdrop} onClick={onClose} />

      <div className={styles.dialog} role="dialog" aria-modal="true">
        <div className={styles.card} onClick={e => e.stopPropagation()}>
        <div className={styles.cardInner}>

          <div className={styles.header}>
            <h3 className={styles.title}>{t('confirm_sessions', 'Confirmar sesiones')}</h3>
            <button type="button" className={styles.closeBtn} onClick={onClose} aria-label={t('close', 'Cerrar')}>
              <span className="material-symbols-outlined" style={{ fontSize: 20 }}>close</span>
            </button>
          </div>

          <div className={styles.slotList}>
            {slots.map((slot, i) => {
              const isEditing = editingIndex === i
              const isRescheduled = slot.available && slot.rescheduled
              const isAvailable = slot.available && !slot.rescheduled
              const isUnavailable = !slot.available

              if (isAvailable) {
                return (
                  <div key={`${slot.day}-${slot.time}-${i}`} className={styles.slotCard}>
                    <div className={styles.slotCardLeft}>
                      <span className={cx('material-symbols-outlined', styles.slotIcon)}>check_circle</span>
                      <div className={styles.slotTextBlock}>
                        <p className={styles.slotLabel}>{t('session_n_of_total', 'Sesión {{n}}/{{total}}', { n: i + 1, total: slots.length })}</p>
                        <p className={styles.slotDate}>{formatSlotDate(slot.day, slot.time)}</p>
                      </div>
                    </div>
                    <span className={styles.chipAvailable}>{t('available', 'Disponible')}</span>
                  </div>
                )
              }

              if (isRescheduled && !isEditing) {
                return (
                  <div key={`${slot.day}-${slot.time}-${i}`} className={cx(styles.slotCard, styles.slotCardRescheduled)}>
                    <div className={styles.slotCardLeft}>
                      <span className={cx('material-symbols-outlined', styles.slotIcon)}>check_circle</span>
                      <div className={styles.slotTextBlock}>
                        <p className={styles.slotLabel}>{t('session_n_of_total', 'Sesión {{n}}/{{total}}', { n: i + 1, total: slots.length })}</p>
                        <p className={cx(styles.slotDate, styles.slotDateRescheduled)}>{formatSlotDate(slot.day, slot.time)}</p>
                      </div>
                    </div>
                    <button type="button" className={styles.reschedulBtnIcon} onClick={() => openEdit(i, slot.day, slot.time)} aria-label={t('change', 'Cambiar')}>
                      <span className="material-symbols-outlined" style={{ fontSize: 18 }}>edit_square</span>
                    </button>
                  </div>
                )
              }

              if (isUnavailable && !isEditing) {
                return (
                  <div key={`${slot.day}-${slot.time}-${i}`} className={cx(styles.slotCard, styles.slotCardUnavailable)}>
                    <div className={styles.slotCardLeft}>
                      <span className={cx('material-symbols-outlined', styles.slotIcon, styles.slotIconUnavailable)}>warning</span>
                      <div className={styles.slotTextBlock}>
                        <p className={styles.slotLabel}>{t('session_n_of_total', 'Sesión {{n}}/{{total}}', { n: i + 1, total: slots.length })}</p>
                        <p className={cx(styles.slotDate, styles.slotDateUnavailable)}>{formatSlotDate(slot.day, slot.time)}</p>
                        <p className={styles.slotReason}>{reasonLabel(slot.reason)}</p>
                      </div>
                    </div>
                    <button type="button" className={styles.reschedulBtn} onClick={() => openEdit(i, slot.day)}>
                      {t('reschedule', 'Reprogramar')}
                    </button>
                  </div>
                )
              }

              // isEditing (cubre tanto unavailable como rescheduled)
              const picker = (
                <div className={styles.pickerSection}>
                  {isUnavailable && (
                    <div className={styles.troubleNotice}>
                      <span className="material-symbols-outlined" style={{ fontSize: 16, color: '#c0601e', flexShrink: 0 }}>error</span>
                      <p className={styles.troubleText}>{reasonLabel(slot.reason)}</p>
                    </div>
                  )}
                  <div className={styles.rescheduleRow}>
                    <div className={styles.rescheduleField}>
                      <input
                        ref={dateInputRef}
                        type="date"
                        className={styles.rescheduleDateInput}
                        value={editDay}
                        min={today}
                        onChange={e => handleDayChange(e.target.value)}
                      />
                      <span
                        className="material-symbols-outlined"
                        style={{ fontSize: 20, color: '#6b7280', flexShrink: 0, cursor: 'pointer' }}
                        onClick={() => dateInputRef.current?.showPicker()}
                      >calendar_today</span>
                    </div>
                    <div className={styles.rescheduleField}>
                      <input
                        type="time"
                        className={styles.rescheduleTimeInput}
                        value={editTime}
                        onChange={e => setEditTime(e.target.value)}
                      />
                      <span className="material-symbols-outlined" style={{ fontSize: 20, color: '#6b7280', flexShrink: 0, pointerEvents: 'none' }}>schedule</span>
                    </div>
                    <button
                      type="button"
                      className={styles.confirmBtn}
                      disabled={!editDay || !editTime}
                      onClick={confirmReschedule}
                    >
                      <span className="material-symbols-outlined" style={{ fontSize: 18 }}>chevron_right</span>
                    </button>
                  </div>
                  {editLoading && <span className="spinner-border spinner-border-sm text-secondary" />}
                  {!editLoading && editDay && editTimes.length === 0 && (
                    <p className={styles.noTimesNote}>{t('no_times_for_day', 'Sin horarios disponibles para este día')}</p>
                  )}
                  {!editLoading && editTimes.length > 0 && (
                    <div className={styles.timeSlotsRow}>
                      {editTimes.map(t => (
                        <button
                          key={t}
                          type="button"
                          className={cx(styles.timeSlotBtn, { [styles.timeSlotBtnActive]: t === editTime })}
                          onClick={() => handleTimeSelect(t)}
                        >
                          {t}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )

              if (isRescheduled) return (
                <div key={`${slot.day}-${slot.time}-${i}`} className={cx(styles.slotCardExpandedWrapper, styles.slotCardExpandedRescheduled)}>
                  <div className={styles.slotCardEditingHeader}>
                    <div className={styles.slotCardLeft}>
                      <span className={cx('material-symbols-outlined', styles.slotIcon)}>check_circle</span>
                      <div className={styles.slotTextBlock}>
                        <p className={styles.slotLabel}>{t('session_n_of_total', 'Sesión {{n}}/{{total}}', { n: i + 1, total: slots.length })}</p>
                        <p className={styles.slotDate}>{formatSlotDate(slot.day, slot.time)}</p>
                      </div>
                    </div>
                    <button type="button" className={styles.reschedulBtn} onClick={() => setEditingIndex(null)}>
                      {t('change', 'Cambiar')}
                    </button>
                  </div>
                  {picker}
                </div>
              )

              return (
                <div key={`${slot.day}-${slot.time}-${i}`} className={styles.slotCardExpandedWrapper}>
                  <div className={styles.slotCardEditingHeader}>
                    <div className={styles.slotCardLeft}>
                      <span className={cx('material-symbols-outlined', styles.slotIcon, styles.slotIconUnavailable)}>warning</span>
                      <div className={styles.slotTextBlock}>
                        <p className={styles.slotLabel}>{t('session_n_of_total', 'Sesión {{n}}/{{total}}', { n: i + 1, total: slots.length })}</p>
                        <p className={cx(styles.slotDate, styles.slotDateUnavailable)}>{formatSlotDate(slot.day, slot.time)}</p>
                        <p className={styles.slotReason}>{reasonLabel(slot.reason)}</p>
                      </div>
                    </div>
                    <button type="button" className={styles.reschedulBtn} onClick={() => setEditingIndex(null)}>
                      {t('reschedule', 'Reprogramar')}
                    </button>
                  </div>
                  {picker}
                </div>
              )
            })}
          </div>

          <div>
            {hasConflicts && !allUnavailable && (
              <p className={styles.helperNote}>{t('will_create_available_only', 'Se crearán solo los turnos disponibles.')}</p>
            )}
            <div className={styles.footer}>
              <button type="button" className={styles.btnBack} onClick={onClose} disabled={loading}>
                {t('back', 'Volver')}
              </button>
              {!allUnavailable && (
                <button type="button" className={styles.btnConfirm} onClick={onConfirm} disabled={loading}>
                  {loading && <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />}
                  {hasConflicts ? t('create_available', 'Crear disponibles') : t('create_sessions', 'Crear sesiones')}
                </button>
              )}
            </div>
          </div>

        </div>
        </div>
      </div>
    </>
  )

  return createPortal(modal, document.body)
}

export default BatchSessionsModal
